import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import axios from 'axios';
import { ROLES, VISIBLE_ROLES, ROLE_DISPLAY_NAMES, isFlightCrewRole } from '../../constants/roles';
import {
  emptyCrewClearanceFormValues,
  appendCrewClearancePayload
} from '../../utils/crewClearance';
import CrewClearanceFields from './CrewClearanceFields';

const emptyWorkLocation = () => ({
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateRegion: '',
  postalCode: '',
  country: ''
});

const OnboardingRequestForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: ROLES.CREW,
    gender: '',
    workLocation: emptyWorkLocation(),
    ...emptyCrewClearanceFormValues(),
    department: '',
    phone: '',
    remarks: '',
    bandId: ''
  });
  const [bands, setBands] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/users/onboard/bands');
        if (!cancelled) setBands(res.data || []);
      } catch {
        if (!cancelled) setBands([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleWorkLocationChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      workLocation: { ...prev.workLocation, [field]: value }
    }));
  };

  const handleRoleChange = (event) => {
    const role = event.target.value;
    setFormData((prev) => ({
      ...prev,
      role,
      ...(isFlightCrewRole(role) ? {} : emptyCrewClearanceFormValues()),
      // Clear band when switching to roles that cannot join HRMS bands
      ...([ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.CLIENT, ROLES.EMPLOYEE, ROLES.EXTERNAL_OPERATOR].includes(role) ? { bandId: '' } : {})
    }));
  };

  const canAssignBands = ![ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.CLIENT, ROLES.EMPLOYEE, ROLES.EXTERNAL_OPERATOR].includes(formData.role);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      role: formData.role,
      workLocation: formData.workLocation,
      department: formData.department || undefined,
      phone: formData.phone.trim(),
      remarks: formData.remarks || undefined
    };
    if (formData.gender) {
      payload.gender = formData.gender;
    }
    if (canAssignBands && formData.bandId) {
      payload.bandIds = [formData.bandId];
    }
    appendCrewClearancePayload(payload, formData.role, formData);

    try {
      await axios.post('/api/users/onboard', payload);
      navigate('/users/onboard');
    } catch (err) {
      console.error('Onboarding create error:', err);
      const msg = err.response?.data?.errors?.[0]?.msg
        || err.response?.data?.message
        || 'Failed to create onboarding request.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" mb={3}>New Onboarding Request</Typography>
      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name (optional)"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Gender (optional)"
                value={formData.gender}
                onChange={handleChange('gender')}
                fullWidth
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Role"
                value={formData.role}
                onChange={handleRoleChange}
                fullWidth
              >
                {VISIBLE_ROLES.map((role) => {
                  const label = ROLE_DISPLAY_NAMES[role] || role.replace(/_/g, ' ');
                  return (
                    <MenuItem key={role} value={role}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>
            {isFlightCrewRole(formData.role) && (
              <CrewClearanceFields
                formData={formData}
                onChange={handleChange}
                showNotSpecified
              />
            )}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                Base work / reporting location
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                All location fields are optional.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 1"
                value={formData.workLocation.addressLine1}
                onChange={handleWorkLocationChange('addressLine1')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address line 2 (optional)"
                value={formData.workLocation.addressLine2}
                onChange={handleWorkLocationChange('addressLine2')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="City (optional)"
                value={formData.workLocation.city}
                onChange={handleWorkLocationChange('city')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="State / region (optional)"
                value={formData.workLocation.stateRegion}
                onChange={handleWorkLocationChange('stateRegion')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Postal code (optional)"
                value={formData.workLocation.postalCode}
                onChange={handleWorkLocationChange('postalCode')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Country (optional)"
                value={formData.workLocation.country}
                onChange={handleWorkLocationChange('country')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Department"
                value={formData.department}
                onChange={handleChange('department')}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Phone"
                value={formData.phone}
                onChange={handleChange('phone')}
                fullWidth
                required
              />
            </Grid>
            {canAssignBands && bands.length > 0 && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="onboard-band-label">Band / group (optional)</InputLabel>
                  <Select
                    labelId="onboard-band-label"
                    label="Band / group (optional)"
                    value={formData.bandId}
                    onChange={handleChange('bandId')}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {bands.map((band) => (
                      <MenuItem key={band._id} value={band._id}>
                        {[band.name, band.code, band.department].filter(Boolean).join(' · ')}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  An employee can belong to only one band. You can also assign later on Staff details → Bands.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Remarks"
                value={formData.remarks}
                onChange={handleChange('remarks')}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Onboarding'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/users/onboard')} disabled={submitting}>
              Cancel
            </Button>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default OnboardingRequestForm;
